import { useState, type FormEvent } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Can } from '@/auth/guards';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/api/core/client';
import { resolveOrderAction, type OrderDocHint } from '@/api/orders';
import { chooseOption, type TodayItem, type TodayOption } from '@/api/today';
import { UploadDocumentButton } from '@/features/orders/workLib';

const DOC_HINTS = new Set<OrderDocHint>(['invoice', 'order_form', 'product_list', 'duty_invoice', 'other']);

/** Why a tap did not go through, in the server's words. */
function failure(error: unknown): string {
  if (error instanceof ApiError) {
    const d = error.details as { executed?: { message?: string }; details?: string[]; error?: string } | null;
    if (d?.executed?.message) return d.executed.message;
    if (Array.isArray(d?.details) && d.details.length) return d.details.join('; ');
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

/** The inline form a button opens: its declared inputs, or Matt's own words. */
type Pending = { option: TodayOption } | { other: true };

/**
 * An ask's buttons, wherever it shows (Today, To decide): its options, then
 * "Something else…" (choose `_other` with text); an errand gets Done and its
 * upload, filed as the errand's own doc_hint so the upload closes it.
 * `children` trail the buttons (Today's open link and Claude button).
 */
export function AskActions({ item, onChanged, children }: {
  item: TodayItem;
  onChanged: () => void;
  children?: React.ReactNode;
}) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const askId = item.ask?.id;

  const run = async (key: string, fn: () => Promise<string | null>) => {
    setBusy(key);
    setError(null);
    try {
      const message = await fn();
      setPending(null);
      setText('');
      setValues({});
      if (message) setNotice(message);
      onChanged();
    } catch (e) {
      setError(failure(e));
    } finally {
      setBusy(null);
    }
  };

  const choose = (option: TodayOption, inputs?: Record<string, string>) => run(option.key, async () => {
    let r;
    try {
      r = await chooseOption(askId!, option.key, { inputs });
    } catch (e) {
      // A second tap on a kept-open option (the draft already exists): the
      // server did nothing, and the reload shows what the first tap made.
      if (e instanceof ApiError && (e.details as { error?: string } | null)?.error === 'already_done') return null;
      throw e;
    }
    // An agent option queues a run and leaves the list; a server option has
    // already done its thing, and the reload shows the result (closed, or
    // still open with new numbers or an Open draft link).
    return r.queued ? 'Sent to Claude — queued for the next run.' : null;
  });

  const tap = (option: TodayOption) => {
    if (option.inputs && Object.keys(option.inputs).length) {
      const today = new Date().toLocaleDateString('en-CA');
      setValues(Object.fromEntries(Object.entries(option.inputs).map(([k, d]) => [k, d.type === 'date' ? today : ''])));
      setPending({ option });
      return;
    }
    void choose(option);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!pending) return;
    if ('option' in pending) {
      const inputs = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim()));
      void choose(pending.option, inputs);
    } else {
      const words = text.trim();
      if (!words) return;
      void run('_other', async () => { await chooseOption(askId!, '_other', { text: words }); return 'Sent to Claude — queued for the next run.'; });
    }
  };

  if (notice) return <div className="text-xs text-success-ink">{notice}</div>;

  const buttons = (() => {
    if (!askId) return null;
    if (item.errand) {
      // An errand has no options: Done is its one close.
      return <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" disabled={!!busy}
        onClick={() => void run('done', async () => { await resolveOrderAction(askId, 'done'); return null; })}>
        {busy === 'done' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}Done
      </Button>;
    }
    return <>
      {item.options.map((o, i) => (
        <Button key={o.key} size="sm" variant={i === 0 ? 'default' : 'outline'} className="h-8 gap-1.5 text-xs" disabled={!!busy} onClick={() => tap(o)}>
          {busy === o.key && <Loader2 className="h-3 w-3 animate-spin" />}{o.label}
        </Button>
      ))}
      <Button size="sm" variant="ghost" className="h-8 text-xs text-muted-foreground" disabled={!!busy} onClick={() => { setText(''); setPending({ other: true }); }}>
        Something else…
      </Button>
    </>;
  })();

  return (
    <div className="flex flex-col gap-2">
      {pending ? (
        <form onSubmit={submit} className="flex flex-wrap items-end gap-2 rounded-md bg-muted/50 p-2.5">
          {'option' in pending ? Object.entries(pending.option.inputs ?? {}).map(([name, d]) => (
            <label key={name} className="grid gap-1 text-xs text-muted-foreground">
              {name === 'placed_date' ? 'Placed on' : name === 'order_number' ? 'Order #' : name.replace(/_/g, ' ')}
              <Input type={d.type === 'date' ? 'date' : 'text'} required={d.required} maxLength={d.max}
                value={values[name] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [name]: e.target.value }))}
                className="h-8 w-44 bg-card text-sm" />
            </label>
          )) : (
            <Textarea autoFocus value={text} onChange={(e) => setText(e.target.value)} maxLength={500} rows={2}
              placeholder="What should happen?" className="min-w-0 flex-[1_1_18rem] bg-card text-sm"
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(e); } }} />
          )}
          <Button type="submit" size="sm" className="h-8 text-xs" disabled={!!busy}>
            {busy && <Loader2 className="h-3 w-3 animate-spin" />}
            {'option' in pending ? pending.option.label.replace(/…$/, '') : 'Send'}
          </Button>
          <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPending(null)}>Cancel</Button>
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          <Can code="orders:write:actions">{buttons}</Can>
          {item.errand?.upload && item.journey && DOC_HINTS.has(item.errand.upload.doc_hint as OrderDocHint) && (
            <UploadDocumentButton orderId={item.journey.id} docHint={item.errand.upload.doc_hint as OrderDocHint} label="Upload it" onUploaded={onChanged} />
          )}
          {children}
        </div>
      )}
      {error && <div className="text-xs text-danger-ink">{error}</div>}
    </div>
  );
}
